/**
 * PlayerSearch Component
 *
 * Search input with results list for finding players by displayName.
 * Shows player stats (W/L) and allows clicking to view profile.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { usePlayerSearch, SearchResultPlayer } from '../../hooks/usePlayerSearch';
import styles from './PlayerSearch.module.css';

/**
 * Props for PlayerSearch component
 */
interface PlayerSearchProps {
  /** Callback when a player is selected from results */
  onPlayerSelect?: (player: SearchResultPlayer) => void;
  /** Placeholder text for search input */
  placeholder?: string;
  /** Maximum results to show */
  limit?: number;
}

/**
 * Spinner component for loading state
 */
function Spinner() {
  return (
    <span className={styles.spinner}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" opacity="0.25" />
        <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
      </svg>
    </span>
  );
}

/**
 * Search icon component
 */
function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/**
 * Clear/X icon component
 */
function ClearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * PlayerSearch Component
 */
export function PlayerSearch({
  onPlayerSelect,
  placeholder = 'Search players...',
  limit = 10
}: PlayerSearchProps) {
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const { search, results, isSearching, error, clearResults } = usePlayerSearch(limit);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Handle input change with debouncing
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(true);

    // Clear previous debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      if (value.trim().length >= 2) {
        search(value);
      } else {
        clearResults();
      }
    }, 300);
  }, [search, clearResults]);

  /**
   * Handle search button click
   */
  const handleSearchClick = useCallback(() => {
    if (query.trim().length >= 2) {
      search(query);
      setShowResults(true);
    }
  }, [query, search]);

  /**
   * Handle clear button click
   */
  const handleClear = useCallback(() => {
    setQuery('');
    clearResults();
    setShowResults(false);
  }, [clearResults]);

  /**
   * Handle player selection
   */
  const handlePlayerClick = useCallback((player: SearchResultPlayer) => {
    if (onPlayerSelect) {
      onPlayerSelect(player);
    }
    setShowResults(false);
  }, [onPlayerSelect]);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  }, [handleSearchClick]);

  /**
   * Close results when clicking outside
   */
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Cleanup debounce timer on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const hasQuery = query.trim().length > 0;
  const hasResults = results.length > 0;
  const showEmpty = hasQuery && !isSearching && !hasResults && query.trim().length >= 2;

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.searchInputWrapper}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => hasQuery && setShowResults(true)}
          aria-label="Search players"
        />

        {/* Clear button - shown when there's a query */}
        {hasQuery && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Clear search"
            title="Clear search"
          >
            <ClearIcon />
          </button>
        )}

        {/* Search button */}
        <button
          type="button"
          className={styles.searchButton}
          onClick={handleSearchClick}
          disabled={isSearching || query.trim().length < 2}
          aria-label="Search"
          title="Search"
        >
          {isSearching ? <Spinner /> : <SearchIcon />}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}

      {/* Results dropdown */}
      {showResults && (hasResults || showEmpty) && (
        <div className={styles.resultsDropdown}>
          {showEmpty ? (
            <div className={styles.emptyState}>No players found</div>
          ) : (
            <ul className={styles.resultsList}>
              {results.map((player) => (
                <li
                  key={player.id}
                  className={styles.resultItem}
                  onClick={() => handlePlayerClick(player)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handlePlayerClick(player)}
                >
                  <span className={styles.playerName}>{player.displayName}</span>
                  <span className={styles.playerStats}>
                    <span className={styles.wins}>{player.stats.wins}W</span>
                    <span className={styles.separator}>/</span>
                    <span className={styles.losses}>{player.stats.losses}L</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default PlayerSearch;
